export const featureSections = [
  {
    slug: "study-reflect",
    title: "Study & Reflect",
    summary: "The main Bible study workspace for reading Scripture carefully and building a habit of reflection.",
    features: [
      "Opens the core Bible-reading experience where users can move through Scripture verse by verse.",
      "Supports thoughtful reflection by pairing reading with note-oriented workflows.",
      "Connects naturally to personal notes and saved study material for ongoing growth.",
    ],
    walkthroughTitle: "How the Study & Reflect experience works",
    walkthroughIntro:
      "This flow is designed to keep the text central while giving users room to slow down, observe, and record what stands out.",
    steps: [
      {
        title: "Enter the study workspace",
        text: "Users arrive in a reading-first environment that frames Bible study as a focused activity rather than a busy dashboard.",
      },
      {
        title: "Move through Scripture verse by verse",
        text: "The reading experience emphasizes deliberate progress so users can stay with the passage instead of rushing past it.",
      },
      {
        title: "Capture reflections as they arise",
        text: "As insight forms, the UX supports moving naturally from reading into note-taking and reflection without breaking concentration.",
      },
      {
        title: "Return and continue the habit",
        text: "The page fits into a larger loop where study sessions can be revisited, extended, and connected to saved notes over time.",
      },
    ],
    outcomes: [
      "Creates a calm reading flow centered on Scripture.",
      "Encourages reflection while the passage is still fresh.",
      "Builds continuity between study sessions and saved notes.",
    ],
  },
  {
    slug: "notes",
    title: "Notes",
    summary: "The personal knowledge space for saving insights, revisiting observations, and organizing study output.",
    features: [
      "Lets users review previously created notes in one place.",
      "Supports deeper study by keeping personal reflections connected to Scripture reading.",
      "Provides detail views for opening and continuing work on individual notes.",
    ],
    walkthroughTitle: "How the Notes experience works",
    walkthroughIntro:
      "The notes flow turns reflection into something durable, giving users a place to return to insights instead of losing them between study sessions.",
    steps: [
      {
        title: "Open your note library",
        text: "Users can see their collected reflections in one place, which makes personal study feel cumulative rather than scattered.",
      },
      {
        title: "Review previous observations",
        text: "The page encourages users to revisit what they already wrote, helping them reconnect with past passages and ideas.",
      },
      {
        title: "Open individual note details",
        text: "From the list view, users can move into a specific note and continue shaping what they have already recorded.",
      },
      {
        title: "Keep study output organized",
        text: "The overall UX treats notes as a living study record, making it easier to build patterns of learning over time.",
      },
    ],
    outcomes: [
      "Gives users a durable place for personal insights.",
      "Makes reflection easier to revisit and deepen.",
      "Supports a longer-term study practice instead of one-off sessions.",
    ],
  },
  {
    slug: "communities",
    title: "Communities",
    summary: "The hub for collaborative study, group interaction, and shared discussion around Scripture.",
    features: [
      "Gives signed-in users access to their community spaces and shared conversations.",
      "Supports posts, replies, polls, and Bible study-based discussion formats.",
      "Creates a place where study moves from private reflection into group engagement.",
    ],
    walkthroughTitle: "How the Communities experience works",
    walkthroughIntro:
      "Communities extend the product from individual study into shared learning, giving users a place to interact around Scripture together.",
    steps: [
      {
        title: "Enter a collaborative space",
        text: "Users move from solo study into a group environment where conversation and shared activity are visible.",
      },
      {
        title: "Browse shared content",
        text: "The community feed supports different post types so groups can discuss, ask questions, vote, and reflect together.",
      },
      {
        title: "Participate in discussion",
        text: "Replies and thread interactions make it possible to respond directly to what others are studying or sharing.",
      },
      {
        title: "Stay engaged over time",
        text: "The UX is built to support ongoing group momentum rather than one-time interactions, keeping study active at the community level.",
      },
    ],
    outcomes: [
      "Turns Bible study into shared conversation.",
      "Supports multiple interaction styles inside one community space.",
      "Helps users stay connected to a group rhythm of study.",
    ],
  },
  {
    slug: "community-discovery",
    title: "Community Discovery",
    summary: "The set of pages that help users understand, join, and build communities.",
    features: [
      "Explains the community experience for visitors who are not yet active members.",
      "Lets users browse available communities and review community information before joining.",
      "Supports creation of new communities for leaders who want to gather a group around shared study.",
    ],
    walkthroughTitle: "How Community Discovery works",
    walkthroughIntro:
      "This part of the product lowers the barrier to entry by helping users understand what communities are for before they commit to one.",
    steps: [
      {
        title: "Learn how communities work",
        text: "Visitors first get context for the product's community model so the experience feels approachable and intentional.",
      },
      {
        title: "Browse available groups",
        text: "Users can explore options and compare community spaces before deciding where they belong.",
      },
      {
        title: "Review community information",
        text: "The UX surfaces enough detail for users to understand a community's identity before they join or request access.",
      },
      {
        title: "Create a new group when needed",
        text: "Leaders are given a path to start their own community when the right space does not already exist.",
      },
    ],
    outcomes: [
      "Makes communities easier to discover and understand.",
      "Reduces friction for first-time participants.",
      "Supports both joining and founding a group.",
    ],
  },
  {
    slug: "my-community-workspace",
    title: "My Community Workspace",
    summary: "The operational area for a specific community where members read updates, manage participation, and post content.",
    features: [
      "Shows the overview and ongoing activity for a selected community.",
      "Supports leader workflows such as membership management and Bible study publishing.",
      "Provides post detail pages where members can reply, reflect, vote, and continue discussion.",
    ],
    walkthroughTitle: "How the My Community workspace works",
    walkthroughIntro:
      "Once inside a community, users need a practical workspace where conversations, leadership actions, and study content can all live together.",
    steps: [
      {
        title: "Open a specific community",
        text: "Users enter a dedicated space centered on one group, which helps conversation and activity stay context-specific.",
      },
      {
        title: "See the current state of the group",
        text: "Overview information and post activity give members an immediate sense of what is happening in the community.",
      },
      {
        title: "Take part or lead",
        text: "Members can engage with posts while leaders get additional workflows for publishing content and managing participation.",
      },
      {
        title: "Continue deeper into post details",
        text: "Detailed post views allow users to move from scanning activity into direct engagement with discussion and study content.",
      },
    ],
    outcomes: [
      "Keeps each group organized around its own activity.",
      "Supports both member participation and leadership tasks.",
      "Creates a clear handoff from overview into discussion.",
    ],
  },
  {
    slug: "community-bible-study",
    title: "Community Bible Study",
    summary: "A structured teaching and response flow for leaders and members who want a guided group study experience.",
    features: [
      "Allows leaders to build Bible study posts with passages, notes, reflection prompts, and discussion questions.",
      "Lets members submit guided shares that respond to the study in a structured format.",
      "Keeps study discussions easier to follow by separating passage content, prompts, and participant responses.",
    ],
    walkthroughTitle: "How Community Bible Study works",
    walkthroughIntro:
      "This is the most guided community learning flow in the product, giving leaders a way to teach and members a way to respond with structure.",
    steps: [
      {
        title: "Leaders compose the study",
        text: "A leader creates a post that combines Scripture, notes, prompts, and questions into one guided study experience.",
      },
      {
        title: "Members read the study in context",
        text: "Participants see the passage, leader framing, and discussion prompts in a single flow that is easier to follow than a free-form thread.",
      },
      {
        title: "Members submit a guided share",
        text: "Instead of one unstructured reply, the UX walks users through reflection and question-by-question responses.",
      },
      {
        title: "The community reviews organized responses",
        text: "Because each share is structured, members can read other responses more clearly and follow the study discussion with less friction.",
      },
    ],
    outcomes: [
      "Supports leader-led Bible studies with clear structure.",
      "Makes member responses easier to read and compare.",
      "Improves the clarity of group discussion around a passage.",
    ],
  },
];

export const featureSectionMap = new Map(featureSections.map((section) => [section.slug, section]));
